import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './LoginButton.css';

const LoginButton = () => {
  const { login, loading, error, apiConnected } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  // Handle Google Sign-In response
  const handleGoogleSignIn = useCallback(async (response) => {
    if (!response.credential) {
      console.error('No credential received from Google');
      return;
    }

    setIsLoggingIn(true);

    try {
      console.log('🔐 Processing Google sign-in...');

      // Send the credential to your backend
      const result = await login({
        credential: response.credential,
        clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID
      });

      if (result.success) {
        console.log('✅ Google sign-in successful!');
      } else {
        console.error('❌ Google sign-in failed:', result.error);
      }
    } catch (error) {
      console.error('❌ Sign-in error:', error);
    } finally {
      setIsLoggingIn(false);
    }
  }, [login]);

  // Load Google Sign-In API
  useEffect(() => {
    const loadGoogleAPI = () => {
      if (window.google) {
        initializeGoogleSignIn();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      script.onerror = () => {
        console.error('Failed to load Google Sign-In API');
      };
      document.head.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleSignIn,
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm_for_prompt: false,
          });
          setGoogleLoaded(true);
          console.log('✅ Google Sign-In API loaded');
        } catch (error) {
          console.error('❌ Failed to initialize Google Sign-In:', error);
        }
      }
    };

    if (apiConnected) {
      loadGoogleAPI();
    }

    return () => {
      // Cleanup: remove script if component unmounts
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [apiConnected, handleGoogleSignIn]);

  // Trigger Google Sign-In popup
  const handleGoogleLogin = () => {
    if (!googleLoaded || !window.google) {
      console.error('Google Sign-In API not loaded');
      alert('Google Sign-In is loading. Please try again in a moment.');
      return;
    }

    try {
      // Try direct button rendering instead of prompt
      const buttonContainer = document.getElementById('google-signin-button');
      if (buttonContainer) {
        buttonContainer.style.display = 'block';
        buttonContainer.innerHTML = ''; // Clear previous button

        window.google.accounts.id.renderButton(buttonContainer, {
          theme: 'filled_blue',
          size: 'large',
          shape: 'rectangular',
          width: 250,
          text: 'signin_with',
          logo_alignment: 'left'
        });
      }
    } catch (error) {
      console.error('Error rendering Google Sign-In button:', error);
      alert('Google Sign-In not available. Please check your internet connection.');
    }
  };

  if (!apiConnected) {
    return (
      <div className="login-container">
        <button className="login-google-btn" disabled>
          🔌 API Not Connected
        </button>
        <p className="login-note">
          Please start your backend server first
        </p>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Real Google Sign-In Button */}
      <button
        className="login-google-btn primary"
        onClick={handleGoogleLogin}
        disabled={loading || isLoggingIn || !googleLoaded}
      >
        {isLoggingIn ? '🔄 Signing in...' :
          !googleLoaded ? '⏳ Loading Google...' :
            '🔑 Sign in with Google'}
      </button>

      {/* Google Sign-In Button Container */}
      <div id="google-signin-button" style={{ display: 'none', marginTop: '0.5rem' }}></div>

      {error && (
        <div className="login-error">
          <strong>Login Error:</strong> {error}
        </div>
      )}

      <div className="login-info">
        <p className="login-note">
          Click the button above to sign in with your Google account
        </p>
        <p className="login-note">
          <em>Note: Google OAuth may not work on localhost without proper domain configuration</em>
        </p>
      </div>

      {!googleLoaded && apiConnected && (
        <div className="loading-google">
          <small>Loading Google Sign-In...</small>
        </div>
      )}
    </div>
  );
};

export default LoginButton;